import React, { useState } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Target, 
  Globe, 
  PieChart, 
  Users, 
  DollarSign, 
  ArrowLeft,
  Activity,
  AlertTriangle,
  CheckCircle,
  Eye
} from 'lucide-react';
import { marketAnalysis } from '../../data/salesCycleData';

const MarketAnalysis = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const renderWorkflowBanner = () => {
    if (navigationSource === 'sales-workflow') {
      return (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-800">來自業務流程: 銷售週期管理</h3>
                <p className="text-sm text-blue-600">階段A: 潛在客戶識別與開發 - 市場分析</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回銷售週期</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">市場分析儀表板</h2>
      </div>

      {/* 市場概況 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">總市場規模</p>
              <p className="text-2xl font-bold text-gray-800">
                {formatCurrency(marketAnalysis.market_overview.total_market_size)}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Globe className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">市場成長率</p>
              <p className="text-2xl font-bold text-gray-800">
                {marketAnalysis.market_overview.market_growth_rate}%
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">主要競爭者</p>
              <p className="text-2xl font-bold text-gray-800">
                {marketAnalysis.competitive_analysis.length}
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">市場趨勢</p>
              <p className="text-2xl font-bold text-gray-800">
                {marketAnalysis.market_trends.length}
              </p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <BarChart3 className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* 市場區隔分析 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">市場區隔分析</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {marketAnalysis.market_overview.market_segments.map((segment, index) => (
            <div 
              key={index}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
              onClick={() => {
                setSelectedSegment(segment);
                setActiveView('segment_detail');
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-800">{segment.segment_name}</h4>
                <span className="text-sm font-medium text-blue-600">{segment.market_share}%</span>
              </div>
              <div className="mb-2">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${segment.market_share}%` }}
                  ></div>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>規模: {formatCurrency(segment.size)}</span>
                <span className="text-green-600">+{segment.growth_rate}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 競爭分析 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">競爭對手分析</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  競爭對手
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  市場占有率
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  競爭優勢
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  競爭劣勢
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  定價策略
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {marketAnalysis.competitive_analysis.map((competitor, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{competitor.competitor_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{competitor.market_share}%</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {competitor.strengths.map((strength, i) => (
                        <span key={i} className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mr-1 mb-1">
                          {strength}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {competitor.weaknesses.map((weakness, i) => (
                        <span key={i} className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full mr-1 mb-1">
                          {weakness}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      competitor.pricing_strategy === 'cost_leadership' ? 'bg-blue-100 text-blue-800' :
                      competitor.pricing_strategy === 'differentiation' ? 'bg-purple-100 text-purple-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {competitor.pricing_strategy === 'cost_leadership' ? '成本領導' :
                       competitor.pricing_strategy === 'differentiation' ? '差異化' : '集中化'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 市場趨勢 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">市場趨勢</h3>
          <div className="space-y-4">
            {marketAnalysis.market_trends.map((trend, index) => (
              <div key={index} className="border-l-4 border-blue-500 pl-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-800">{trend.trend_name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    trend.impact === 'high' ? 'bg-red-100 text-red-800' :
                    trend.impact === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {trend.impact === 'high' ? '高影響' :
                     trend.impact === 'medium' ? '中影響' : '低影響'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{trend.description}</p>
                <p className="text-sm text-blue-600 mt-1">機會: {trend.opportunity}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">區域分析</h3>
          <div className="space-y-4">
            {marketAnalysis.regional_analysis.map((region, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  setSelectedRegion(region);
                  setActiveView('region_detail');
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-800">{region.region_name}</h4>
                  <span className="text-sm font-medium text-blue-600">{region.market_share}%</span>
                </div>
                <div className="mb-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${region.market_share}%` }}
                    ></div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>成長率: {region.growth_rate}%</span>
                  <button className="text-blue-600 hover:text-blue-800">
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSegmentDetail = () => {
    if (!selectedSegment) return null;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">市場區隔詳細分析</h2>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">{selectedSegment.segment_name}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-600">市場占有率</p>
              <p className="text-2xl font-bold text-blue-600">{selectedSegment.market_share}%</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">市場規模</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedSegment.size)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">成長率</p>
              <p className="text-2xl font-bold text-purple-600">{selectedSegment.growth_rate}%</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">趨勢</p>
              <p className="text-2xl font-bold text-yellow-600">
                {selectedSegment.growth_rate > 10 ? '快速成長' : 
                 selectedSegment.growth_rate > 5 ? '穩定成長' : '緩慢成長'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRegionDetail = () => {
    if (!selectedRegion) return null;

    return (
      <div className="space-y-6">
        {renderWorkflowBanner()}
        
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">區域市場詳細分析</h2>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">{selectedRegion.region_name}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">市場占有率</p>
              <p className="text-2xl font-bold text-blue-600">{selectedRegion.market_share}%</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">成長率</p>
              <p className="text-2xl font-bold text-green-600">{selectedRegion.growth_rate}%</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">主要國家</p>
              <p className="text-lg font-bold text-purple-600">{selectedRegion.key_countries.length}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">主要國家</h4>
              <div className="space-y-2">
                {selectedRegion.key_countries.map((country, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-gray-800">{country}</span>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-800 mb-3">市場機會</h4>
              <div className="space-y-2">
                {selectedRegion.opportunities.map((opportunity, index) => (
                  <div key={index} className="flex items-center space-x-2 p-2 bg-blue-50 rounded">
                    <Target className="w-4 h-4 text-blue-600" />
                    <span className="text-gray-800">{opportunity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'segment_detail':
        return renderSegmentDetail();
      case 'region_detail':
        return renderRegionDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default MarketAnalysis; 